import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box, Button, Checkbox, CircularProgress, FormControlLabel, FormHelperText, IconButton, MenuItem, Paper, Radio,
  RadioGroup, Table, TableBody, TableCell, TableHead, TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import { Icon } from '@iconify/react';
import InlineAlert from '../../../components/inlineAlert';
import { resultMessage } from '../../../../service/apiResult';
import { managementPageSubtitleSx, managementPageTitleSx } from '../../../styles/managementStyles';
import { getInitiativePlan, getInitiativePlanOptions, saveInitiativePlan } from './initiativePlans.service';
import {
  MODELS, PAYMENT_PROVIDERS, describeRange, emptyDraft, emptyTier, formatMoney, fromPlan, minUnitsOf, toRequest,
  validateDraft,
} from './initiativePlanRules';

const sectionSx = { p: { xs: 2, md: 3 }, mb: 3, borderRadius: 2 };
const sectionTitleSx = { fontSize: 18, fontWeight: 600, mb: 2 };

/** Create or edit one Initiative plan (route /addInitiativePlan or /addInitiativePlan/:id). */
export default function InitiativePlanForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [draft, setDraft] = useState(emptyDraft());
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showErrors, setShowErrors] = useState(false);
  const [serverError, setServerError] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [opts, plan] = await Promise.all([getInitiativePlanOptions(), id ? getInitiativePlan(id) : null]);
      if (cancelled) return;
      if (opts.success) setOptions(opts.data);
      else setServerError(resultMessage(opts, 'Could not load the Initiatives.'));
      if (plan) {
        if (plan.success) setDraft(fromPlan(plan.data));
        else setServerError(resultMessage(plan, 'Could not load this plan.'));
      }
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [id]);

  const { errors } = useMemo(() => validateDraft(draft), [draft]);
  const shown = showErrors ? errors : {};
  const initiative = options.find((o) => o.id === draft.initiativeId);
  const countries = initiative?.countries || [];
  const country = countries.find((c) => c.id === draft.countryId);
  const model = MODELS[draft.pricingModel];
  const isEnterprise = draft.pricingModel === 'ENTERPRISE';
  const contactUs = isEnterprise && draft.enterpriseMode === 'CONTACT_US';
  const mins = minUnitsOf(draft.tiers);
  const editing = Boolean(id);

  const set = (patch) => setDraft((d) => ({ ...d, ...patch }));
  const setTier = (i, patch) => setDraft((d) => ({ ...d, tiers: d.tiers.map((t, k) => (k === i ? { ...t, ...patch } : t)) }));

  const setFeature = (i, f, value) => setTier(i, { features: draft.tiers[i].features.map((x, k) => (k === f ? value : x)) });
  const addFeature = (i) => setTier(i, { features: [...draft.tiers[i].features, ''] });
  const removeFeature = (i, f) => setTier(i, { features: draft.tiers[i].features.filter((_, k) => k !== f) });

  const addTier = () => setDraft((d) => {
    const tiers = d.tiers.map((t, k) => (k === d.tiers.length - 1 ? { ...t, open: false } : t));
    return { ...d, tiers: [...tiers, { ...emptyTier(), open: true }] };
  });
  const removeTier = (i) => setDraft((d) => {
    const tiers = d.tiers.filter((_, k) => k !== i);
    if (tiers.length) tiers[tiers.length - 1] = { ...tiers[tiers.length - 1], open: true };
    return { ...d, tiers };
  });

  const onInitiative = (value) => {
    const next = options.find((o) => o.id === value);
    const keep = next?.countries.some((c) => c.id === draft.countryId);
    set({ initiativeId: value, countryId: keep ? draft.countryId : (next?.countries.length === 1 ? next.countries[0].id : '') });
  };

  const submit = async (event) => {
    event.preventDefault();
    setShowErrors(true);
    setServerError('');
    if (Object.keys(errors).length) return;
    setSaving(true);
    const result = await saveInitiativePlan(toRequest(draft), id);
    setSaving(false);
    if (result.success) navigate('/subscriptionPlans?tab=initiative');
    else setServerError(resultMessage(result, 'Could not save the plan.'));
  };

  if (loading) {
    return <Box sx={{ py: 8, textAlign: 'center' }}><CircularProgress aria-label="Loading" /></Box>;
  }

  const priceField = (i, field, label) => (
    <TextField
      size="small" label={label} value={draft.tiers[i][field === 'monthlyPrice' ? 'monthly' : 'yearly']}
      onChange={(e) => setTier(i, { [field === 'monthlyPrice' ? 'monthly' : 'yearly']: e.target.value })}
      error={Boolean(shown[`tiers.${i}.${field === 'monthlyPrice' ? 'monthly' : 'yearly'}`])}
      helperText={shown[`tiers.${i}.${field === 'monthlyPrice' ? 'monthly' : 'yearly'}`] || ' '}
      inputProps={{ inputMode: 'decimal', 'aria-label': `Tier ${i + 1} ${label}` }}
      InputProps={{ startAdornment: country?.currencyCode ? <Typography sx={{ mr: 0.5, color: '#6C6A86' }}>{country.currencySymbol || country.currencyCode}</Typography> : undefined }}
      sx={{ minWidth: 150 }}
    />
  );

  return (
    <Box component="form" onSubmit={submit} noValidate>
      <Box sx={{ mb: 4 }}>
        <Typography className="app-page-title" sx={managementPageTitleSx}>
          {editing ? 'Edit Initiative plan' : 'Create Initiative plan'}
        </Typography>
        <Typography sx={managementPageSubtitleSx}>
          What an area-based integrator sees when it joins this Initiative in this country.
        </Typography>
      </Box>

      <InlineAlert message={serverError} onClose={() => setServerError('')} variant="error" />
      {showErrors && Object.keys(errors).length > 0 && (
        <InlineAlert message="Some fields need attention. They are marked below." onClose={() => setShowErrors(false)} variant="error" />
      )}

      <Paper variant="outlined" sx={sectionSx}>
        <Typography sx={sectionTitleSx}>Where it applies</Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
          <TextField select required label="Initiative" value={draft.initiativeId} disabled={editing}
            onChange={(e) => onInitiative(e.target.value)} error={Boolean(shown.initiativeId)}
            helperText={shown.initiativeId || (editing ? 'The Initiative of a plan cannot be changed.' : ' ')}>
            {options.map((o) => <MenuItem key={o.id} value={o.id}>{o.name}</MenuItem>)}
          </TextField>
          <TextField select required label="Country" value={draft.countryId} disabled={editing || !initiative}
            onChange={(e) => set({ countryId: e.target.value })} error={Boolean(shown.countryId)}
            helperText={shown.countryId || (!initiative ? 'Choose an Initiative first.' : (country ? `Prices are in ${country.currencyCode || 'the country currency'}.` : ' '))}>
            {countries.map((c) => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
          </TextField>
          <TextField required label="Plan name" value={draft.name} onChange={(e) => set({ name: e.target.value })}
            error={Boolean(shown.name)} helperText={shown.name || ' '} inputProps={{ maxLength: 255 }} />
          <TextField select required label="Payment provider" value={draft.paymentProvider}
            onChange={(e) => set({ paymentProvider: e.target.value })} error={Boolean(shown.paymentProvider)}
            helperText={shown.paymentProvider || 'Used when payment opens; nothing is charged yet.'}>
            {PAYMENT_PROVIDERS.map((p) => <MenuItem key={p.value} value={p.value}>{p.label}</MenuItem>)}
          </TextField>
          <TextField label="Description (optional)" value={draft.description} multiline minRows={2}
            onChange={(e) => set({ description: e.target.value })} error={Boolean(shown.description)}
            helperText={shown.description || ' '} inputProps={{ maxLength: 500 }} sx={{ gridColumn: { md: '1 / -1' } }} />
        </Box>
      </Paper>

      <Paper variant="outlined" sx={sectionSx}>
        <Typography sx={sectionTitleSx} id="pricing-model-label">How it is priced</Typography>
        <RadioGroup aria-labelledby="pricing-model-label" value={draft.pricingModel}
          onChange={(e) => set({ pricingModel: e.target.value })}
          sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 2 }}>
          {Object.entries(MODELS).map(([value, m]) => (
            <Paper key={value} variant="outlined" sx={{
              px: 2, py: 1.5, borderRadius: 2, borderColor: draft.pricingModel === value ? 'primary.main' : 'divider',
              borderWidth: draft.pricingModel === value ? 2 : 1,
            }}>
              <FormControlLabel value={value} control={<Radio />} sx={{ alignItems: 'flex-start', m: 0, width: '100%' }}
                label={<Box><Typography sx={{ fontWeight: 600 }}>{m.label}</Typography>
                  <Typography sx={{ color: '#6C6A86', fontSize: 14 }}>{m.help}</Typography></Box>} />
            </Paper>
          ))}
        </RadioGroup>
      </Paper>

      {isEnterprise && (
        <Paper variant="outlined" sx={sectionSx}>
          <Typography sx={sectionTitleSx} id="enterprise-mode-label">Enterprise offer</Typography>
          <RadioGroup aria-labelledby="enterprise-mode-label" value={draft.enterpriseMode}
            onChange={(e) => set({ enterpriseMode: e.target.value })}>
            <FormControlLabel value="PRICED" control={<Radio />} label="Set the price here" />
            <FormControlLabel value="CONTACT_US" control={<Radio />} label="Contact us: no price shown, the integrator is asked to get in touch" />
          </RadioGroup>
          {contactUs && (
            <TextField fullWidth multiline minRows={2} label="Message to show (optional)" value={draft.contactNote}
              onChange={(e) => set({ contactNote: e.target.value })} error={Boolean(shown.contactNote)}
              helperText={shown.contactNote || 'For example: write to sales@ for a custom quote.'} inputProps={{ maxLength: 500 }} sx={{ mt: 2 }} />
          )}
        </Paper>
      )}

      {!contactUs && (
        <Paper variant="outlined" sx={sectionSx}>
          <Typography sx={sectionTitleSx}>{isEnterprise ? 'Price' : `Tiers (${model.unit})`}</Typography>
          {!isEnterprise && (
            <Typography sx={{ color: '#6C6A86', mb: 2 }}>
              Each tier has one price for the whole tier. The integrator picks the tier that fits. Tiers run on from 1 with no gaps; the last tier can have no upper limit.
            </Typography>
          )}
          {shown.tiers && <FormHelperText error sx={{ mb: 1 }}>{shown.tiers}</FormHelperText>}
          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small" aria-label="Tiers" sx={{ minWidth: 760 }}>
              {!isEnterprise && (
                <TableHead>
                  <TableRow>
                    <TableCell>From</TableCell><TableCell>Up to</TableCell><TableCell>Monthly price</TableCell>
                    <TableCell>Yearly price</TableCell><TableCell />
                  </TableRow>
                </TableHead>
              )}
              <TableBody>
                {(isEnterprise ? draft.tiers.slice(0, 1) : draft.tiers).map((tier, i) => (
                  <React.Fragment key={i}>
                  <TableRow sx={{ verticalAlign: 'top', '& td': { borderBottom: 'none' } }}>
                    {!isEnterprise && (
                      <>
                        <TableCell sx={{ pt: 2.5 }}>{mins[i] ?? '—'}</TableCell>
                        <TableCell>
                          {tier.open ? (
                            <Typography sx={{ pt: 1 }}>and above</Typography>
                          ) : (
                            <TextField size="small" value={tier.maxUnits} onChange={(e) => setTier(i, { maxUnits: e.target.value })}
                              error={Boolean(shown[`tiers.${i}.maxUnits`])} helperText={shown[`tiers.${i}.maxUnits`] || ' '}
                              inputProps={{ inputMode: 'numeric', 'aria-label': `Tier ${i + 1} upper limit` }} sx={{ width: 120 }} />
                          )}
                          {i === draft.tiers.length - 1 && (
                            <FormControlLabel sx={{ display: 'block' }}
                              control={<Checkbox size="small" checked={tier.open} onChange={(e) => setTier(i, { open: e.target.checked, maxUnits: '' })} />}
                              label="No upper limit" />
                          )}
                        </TableCell>
                      </>
                    )}
                    <TableCell>{priceField(i, 'monthlyPrice', 'Monthly')}</TableCell>
                    <TableCell>{priceField(i, 'yearlyPrice', 'Yearly')}</TableCell>
                    <TableCell>
                      {!isEnterprise && draft.tiers.length > 1 && (
                        <Tooltip title="Remove tier">
                          <IconButton aria-label={`Remove tier ${i + 1}`} onClick={() => removeTier(i)}>
                            <Icon icon="tabler:trash" width="20" height="20" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell colSpan={isEnterprise ? 3 : 5} sx={{ pt: 0 }}>
                      <Typography sx={{ fontSize: 14, fontWeight: 600, mb: 1 }}>
                        Features of {isEnterprise ? 'this plan' : `tier ${i + 1}`} (optional)
                      </Typography>
                      {tier.features.map((feature, f) => (
                        <Box key={f} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', mb: 1 }}>
                          <TextField size="small" fullWidth value={feature} placeholder="For example: Email support"
                            onChange={(e) => setFeature(i, f, e.target.value)}
                            error={Boolean(shown[`tiers.${i}.features.${f}`])} helperText={shown[`tiers.${i}.features.${f}`]}
                            inputProps={{ 'aria-label': `Tier ${i + 1} feature ${f + 1}`, maxLength: 255 }} />
                          <Tooltip title="Remove feature">
                            <IconButton aria-label={`Remove tier ${i + 1} feature ${f + 1}`} onClick={() => removeFeature(i, f)}>
                              <Icon icon="tabler:trash" width="20" height="20" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      ))}
                      <Button size="small" startIcon={<Icon icon="tabler:plus" width="16" height="16" />} onClick={() => addFeature(i)}>
                        Add feature
                      </Button>
                    </TableCell>
                  </TableRow>
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          </Box>
          {!isEnterprise && (
            <Button startIcon={<Icon icon="tabler:plus" width="18" height="18" />} onClick={addTier} sx={{ mt: 1 }}>
              Add tier
            </Button>
          )}
        </Paper>
      )}

      <Paper variant="outlined" sx={{ ...sectionSx, bgcolor: 'action.hover' }} aria-live="polite">
        <Typography sx={sectionTitleSx}>What the integrator will see</Typography>
        {contactUs ? (
          <Typography>Enterprise: Contact us{draft.contactNote ? `. ${draft.contactNote}` : ''}</Typography>
        ) : (
          (isEnterprise ? draft.tiers.slice(0, 1) : draft.tiers).map((tier, i) => (
            <Typography key={i} sx={{ mb: 0.5 }}>
              {isEnterprise ? 'Enterprise' : (mins[i] != null ? describeRange(mins[i], tier.open ? null : Number(tier.maxUnits) || null, model.unit) : `Tier ${i + 1}`)}
              {': '}
              {tier.monthly ? `${formatMoney(tier.monthly, country?.currencyCode)} / month` : ''}
              {tier.monthly && tier.yearly ? ' · ' : ''}
              {tier.yearly ? `${formatMoney(tier.yearly, country?.currencyCode)} / year` : ''}
              {tier.features.filter((f) => f.trim()).length ? ` · ${tier.features.filter((f) => f.trim()).map((f) => f.trim()).join(', ')}` : ''}
            </Typography>
          ))
        )}
      </Paper>

      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
        <Button onClick={() => navigate('/subscriptionPlans?tab=initiative')} disabled={saving}>Cancel</Button>
        <Button type="submit" variant="contained" disabled={saving} sx={{ minWidth: 160 }}>
          {saving ? <CircularProgress size={22} color="inherit" aria-label="Saving" /> : (editing ? 'Save changes' : 'Create plan')}
        </Button>
      </Box>
    </Box>
  );
}
